import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useWindowDimensions } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'

import { skipToken } from '@reduxjs/toolkit/query'
import { addYears, differenceInYears, format, parseISO, subMonths } from 'date-fns'
import { isNil } from 'lodash-es'
import { useTheme } from 'styled-components/native'

import { getSessionsNeedingNotesForEpisode } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/getSessionsNeedingNotesForEpisode'
import { getGuardianInfoForEpisode } from '@lyrahealth-inc/shared-app-logic/src/features/appointments/utils'
import { useGetBenefitsResetDate } from '@lyrahealth-inc/shared-app-logic/src/features/benefits/hooks/useGetBenefitsResetDate'
import {
  ProviderInsightsSubAreaGerundFormatLabels,
  ProviderInsightsSubAreaName,
} from '@lyrahealth-inc/shared-app-logic/src/features/careInsights/constants'
import { CLIENT_SELF_PAY_STATUS } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { PRODUCT_TYPE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/productType'
import { getPossessivePronoun } from '@lyrahealth-inc/shared-app-logic/src/features/common/utils/userUtils'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { Programs } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programConfigs'
import { isEpisodeInsightsProgram } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { useGetCustomerForClient } from '@lyrahealth-inc/shared-app-logic/src/features/providers/hooks/useGetCustomerForClient'
import { useGetNextSessionSummary } from '@lyrahealth-inc/shared-app-logic/src/features/providers/hooks/useGetNextSessionSummary'
import { useGetNextSessionSummaryTitle } from '@lyrahealth-inc/shared-app-logic/src/features/providers/hooks/useGetNextSessionSummaryTitle'
import { useGetBHBSessionCount } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/hooks/useGetBHBSessionCount'
import { useGetEAPSessionCount } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/hooks/useGetEAPSessionCount'
import { useGetHighRiskSessionCount } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/hooks/useGetHighRiskSessionCount'
import { useGetLicenseSessionCount } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/hooks/useGetLicenseSessionCount'
import {
  Appointment,
  type PatientUpcomingAppointment,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { ConcurrentCareEpisode, Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import { BulletIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/BulletIcon'
import { SelfPayStatus } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/selfPayStatus/SelfPayStatus'
import { SummaryDetailProps } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/summaryDetail/SummaryDetail'
import { RecommendedActions } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/recommendedActions/RecommendedActions'
import { BenefitsSummary } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/clientDetailsSummary/BenefitsSummary'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { TasksModule } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/tasksModule/TasksModule'
import { UpcomingSessionsModule } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/upcomingSessionsModule/UpcomingSessionsModule'

import { useAiConversationToggleRows } from './hooks/useAiConversationToggleRows'
import { useCanManageCareTeam } from './hooks/useCanManageCareTeam'
import { useCareServicesSidePanelData } from './hooks/useCareServicesSidePanelData'
import { getTaskEligibleAppointments, useClientTasks } from './hooks/useClientTasks'
import { useConcurrentCareData } from './hooks/useConcurrentCareData'
import { useContactsSidePanelData } from './hooks/useContactsSidePanelData'
import { useEligibilityCapsuleDetails } from './hooks/useEligibilityCapsuleDetails'
import { useGetSponsoredBenefitsSidePanelData } from './hooks/useGetSponsoredBenefitsSidePanelData'
import { useHealthPlanStatus } from './hooks/useHealthPlanStatus'
import { useHPISelfPayStatus } from './hooks/useHPISelfPayStatus'
import { useMedicationsSidePanelData } from './hooks/useMedicationsSidePanelData'
import { useRiskOverview } from './hooks/useRiskOverview'
import { useSelfPayContinuationOfCareStatus } from './hooks/useSelfPayContinuationOfCareStatus'
import { useShowSidePanel } from './hooks/useShowSidePanel'
import { ManageCareTeamModal } from './ManageCareTeamModal'
import { groupPastCare } from './pastCare'
import { PastCareModal } from './PastCareModal'
import { ProviderPairMessagePanel } from './ProviderPairMessagePanel'
import { LyraCareService, SponsoredBenefitsModal } from './SponsoredBenefitsModal'
import { mapNpSessionsToScheduleListItem } from './tasksModule/mapNpSessionsToScheduleListItem'
import { mapProviderTaskToListItem } from './tasksModule/mapProviderTaskToListItem'
import { actions, mixpanelEvents } from '../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../mixpanel/mixpanelTracking'
import { episodeStates, parentIntakeQualtricsUrl, programConfig } from '../../../../common/constants/appConstants'
import {
  CLIENT_HOME,
  CLIENTS_CANCEL_SESSION,
  CLIENTS_NEW_SESSION,
  CLIENTS_RESCHEDULE_SESSION_DETAILS,
  CLIENTS_SESSIONS,
} from '../../../../common/constants/routingConstants'
import { getClientSessionLimitCategory, getClientSessionsRemaining } from '../../../../common/utils/utils'
import { useNavigateToCloseSession } from '../../../../dashboard/openItems/hooks/useNavigateToCloseSession'
import { getAuthUserId } from '../../../../data/auth/authSelectors'
import { getSelectedClientId } from '../../../../data/lyraTherapy/clientSelectors'
import { getLyraTherapyContentsData } from '../../../../data/lyraTherapy/contentSelectors'
import { useEapEligibilityError } from '../../../../hooks/useEapEligibilityError'
import { useIsEngageDAProvider } from '../../../../hooks/useIsEngageDAProvider'
import { getProviderDetailsData } from '../../../../providers/data/providerSelectors'
import { useCareProgressSummary } from '../../../careProgressSummary/hooks/useCareProgressSummary'
import { useGetNotesQuery } from '../../../clientNotes/data/notesApi'
import { useAppointmentsThatNeedNotes } from '../../../clientNotes/hooks/useDAAppointmentSets'
import { useNoteNavigationActions } from '../../../clientNotes/hooks/useNoteNavigationActions'
import { DA_FLEXIBLE_NOTE_NAME } from '../../../clientNotes/utils/noteConstants'
import { setToastContent } from '../../../data/ltToastAutoActions'
import { getLTVideoSessionAppointmentId } from '../../../data/ltVideoSelectors'
import { useAllEpisodesForSelectedClient } from '../../../episodes/data/hooks/useAllEpisodesForSelectedClient'
import { useEpisodesForSelectedClient } from '../../../episodes/data/hooks/useEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../../../episodes/data/hooks/useSelectedEpisodeForClient'
import { useProviderPairCareHistory } from '../../../messages/data/hooks/useProviderPairConversations'
import { useProviderPairMessagingAccess } from '../../../messages/data/hooks/useProviderPairMessagingAccess'
import { useNpSessionsToSchedule } from '../../../npSessions/hooks/useNpSessionsToSchedule'
import { useNpTestPlanEntrypoint } from '../../../npTestPlans/hooks/useNpTestPlanEntrypoint'
import { useNpTestPlanRecommendedActions } from '../../../npTestPlans/hooks/useNpTestPlanRecommendedActions'
import { useGetEpisodeInsightsQuery } from '../../../performance/data/careInsightsApi'
import BenefitsSummaryPDF from '../clientRecords/BenefitsSummaryPDF'
import { setAppointment } from '../data/appointmentsSlice'
import { useClientAppointmentsData } from '../data/hooks/useClientAppointmentsData'
import { useCustomersForClient } from '../data/hooks/useCustomersForClient'
import { usePatientUpcomingAppointments } from '../data/hooks/usePatientUpcomingAppointments'
import { useSelectedClientData } from '../data/hooks/useSelectedClientData'
import { useProgramCoverageBreakdown } from '../hooks/useProgramCoverageBreakdown'

type CoverageSupport = {
  bhbSupported: boolean
  eapSupported: boolean
  specialtyBhbSupported: boolean
}

export type ProgramCoverageBreakdown = {
  blendedCareMeds?: CoverageSupport
  directAccessTherapy?: CoverageSupport
  considerLyraTherapy?: CoverageSupport
  alcoholUseDisorderTherapy?: CoverageSupport
  guidedSelfCareEnabled?: CoverageSupport
  considerMeds?: CoverageSupport
  stressManagement?: CoverageSupport
  coachingForParents?: Partial<CoverageSupport>
}

type BenefitsSummaryContainerProps = {
  setClientSummaryPDFContent?: (content: React.ReactNode) => void
  setForceMessagingHubToOpenPopover?: React.Dispatch<React.SetStateAction<boolean>>
}

const BenefitsSummaryContainer: React.FC<BenefitsSummaryContainerProps> = ({
  setClientSummaryPDFContent,
  setForceMessagingHubToOpenPopover: setForceMessagingHubToOpenPopoverProp,
}) => {
  const { colors } = useTheme()

  const outletContext = useOutletContext<{
    setForceMessagingHubToOpenPopover?: React.Dispatch<React.SetStateAction<boolean>>
    setClientDetailsSelectedEpisode?: (episode: Episode) => void
  }>()
  const setForceMessagingHubToOpenPopover =
    setForceMessagingHubToOpenPopoverProp || outletContext?.setForceMessagingHubToOpenPopover
  const setClientDetailsSelectedEpisode = outletContext?.setClientDetailsSelectedEpisode

  const [modalData, setModalData] = useState<null | JSX.Element>(null)
  const [isSponsoredBenefitsModalVisible, setIsSponsoredBenefitsModalVisible] = useState(false)
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as {
    data: Episode | undefined
  }
  const [isPDFContentInitialized, setIsPDFContentInitialized] = React.useState(false)

  // Derive program config directly from the selected episode to avoid timing issues
  const episodeProgramConfig = selectedEpisode?.program_name ? programConfig[selectedEpisode.program_name] : undefined
  const programName = selectedEpisode?.program_name ?? ''
  const careProgressSummaryData = useCareProgressSummary(setModalData)
  // Only neuropsych episodes get the summary card; every other program keeps the Overview it has.
  const { isVisible: isNpEvaluationVisible } = useNpTestPlanEntrypoint(selectedEpisode)
  const npTestPlanRecommendedActions = useNpTestPlanRecommendedActions(selectedEpisode)
  const concurrentCare: ConcurrentCareEpisode[] = useConcurrentCareData()
  const isDAEngageProvider = useIsEngageDAProvider()

  const { isCareInsightsVisible, enableDAFlexibleNote, isDBTTeensEnabled, isClientHeaderRedesignEnabled } = useFlags()
  const dispatch = useDispatch()
  const location = useLocation()
  const showSidePanel = useShowSidePanel()
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()
  const showPastCare = showSidePanel && canAccessProviderPairs
  const navigate = useNavigate()
  const authProviderId = useSelector(getAuthUserId)

  const isDBTTeensProgram = programName === ProgramNames.DialecticalBehaviorTherapyTeens
  const isLCTTeensProgram = programName === ProgramNames.TeensTherapy
  const shouldHideEligiblePrograms = isDBTTeensEnabled && (isDBTTeensProgram || isLCTTeensProgram)

  const episodeInsightsArgs = selectedEpisode?.id
    ? {
        episodeId: selectedEpisode.id,
        providerId: selectedEpisode.provider_id,
      }
    : undefined
  const { data: episodeInsights } = useGetEpisodeInsightsQuery(episodeInsightsArgs ?? skipToken, {
    skip: !isCareInsightsVisible || !isEpisodeInsightsProgram(selectedEpisode?.program_name),
  })

  const clinicalQualityInsights = useMemo(() => {
    if (!episodeInsights?.summary) return []

    // Find the highest and lowest scoring sub-areas across all competency areas
    let maxScore = -Infinity
    let minScore = Infinity
    let bestSubArea: string | undefined
    let worstSubArea: string | undefined

    for (const competencyArea of Object.values(episodeInsights.summary)) {
      if (!competencyArea?.subAreas) continue

      for (const subArea of competencyArea.subAreas) {
        const score = subArea.averageScore?.value
        if (score == null) continue

        if (score > maxScore && subArea.subAreaName in ProviderInsightsSubAreaGerundFormatLabels) {
          maxScore = score
          bestSubArea = subArea.subAreaName
        }
        if (score < minScore && subArea.subAreaName in ProviderInsightsSubAreaGerundFormatLabels) {
          minScore = score
          worstSubArea = subArea.subAreaName
        }
      }
    }

    const potentialStrength = bestSubArea
      ? ProviderInsightsSubAreaGerundFormatLabels[bestSubArea as ProviderInsightsSubAreaName]
      : undefined
    const areaToAddress = worstSubArea
      ? ProviderInsightsSubAreaGerundFormatLabels[worstSubArea as ProviderInsightsSubAreaName]
      : undefined

    const bulletPoints = []
    if (potentialStrength) {
      bulletPoints.push({
        value:
          'Potential strength to build on: ' + potentialStrength.charAt(0).toUpperCase() + potentialStrength.slice(1),
        testID: 'ClinicalQualityInsights-potentialStrength',
        bulletIcon: <BulletIcon size={4} />,
        textColor: colors.textSecondary,
      })
    }

    if (areaToAddress) {
      bulletPoints.push({
        value:
          'Area that may be helpful to address with this client: ' +
          areaToAddress.charAt(0).toUpperCase() +
          areaToAddress.slice(1),
        testID: 'ClinicalQualityInsights-areaToAddress',
        bulletIcon: <BulletIcon size={4} />,
        textColor: colors.textSecondary,
      })
    }

    return bulletPoints
  }, [episodeInsights, colors.textSecondary])

  const { forceAdultFlowsToHPI, isSelfPayEnabled } = useFlags()
  const intl = useIntl()

  const customers = useCustomersForClient()
  const { data: clientDetails } = useSelectedClientData()
  const clientCustomer = useGetCustomerForClient(customers, clientDetails)
  const benefitsResetDate = useGetBenefitsResetDate(clientDetails)
  const providerDetailsData = useSelector(getProviderDetailsData)
  const browserTimeZone = Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone ?? 'America/Los_Angeles'
  // if timezone is not available through providerDetailsData, fallback to browser timezone
  const providerTimeZone = providerDetailsData?.time_zone || browserTimeZone

  const sessionSummaryDetails = useGetNextSessionSummary(
    clientDetails?.next_appointment,
    selectedEpisode,
    intl,
    providerTimeZone,
  )
  const sessionSummaryTitle = useGetNextSessionSummaryTitle(
    selectedEpisode,
    clientDetails,
    clientDetails?.next_appointment,
  )
  const isLyraSelect = clientDetails?.product_type === PRODUCT_TYPE.SELECT

  const programCoverageBreakdown = useProgramCoverageBreakdown(clientDetails?.employer, clientDetails?.country)

  const sessionCountDetails: SummaryDetailProps[] | undefined = useGetEAPSessionCount(
    forceAdultFlowsToHPI,
    intl,
    clientDetails,
    clientCustomer,
    selectedEpisode,
    programCoverageBreakdown,
  )
  const licenseSessionCount = useGetLicenseSessionCount(intl, clientDetails)

  const bhbSessionCount = useGetBHBSessionCount(intl, clientDetails, selectedEpisode, programCoverageBreakdown)

  const highRiskSessionDetails = useGetHighRiskSessionCount(
    intl,
    clientDetails,
    selectedEpisode,
    programCoverageBreakdown,
  )
  const benefitsSummaryDetails: SummaryDetailProps[] | undefined = useMemo(() => {
    let summaryDetails: SummaryDetailProps[] | undefined
    if (sessionCountDetails !== undefined) {
      summaryDetails = [...sessionCountDetails]
    }
    if (licenseSessionCount?.length) {
      summaryDetails = [...(summaryDetails ?? []), ...licenseSessionCount]
    }
    if (bhbSessionCount !== undefined) {
      if (bhbSessionCount !== null) {
        summaryDetails ? summaryDetails.push(bhbSessionCount) : (summaryDetails = [bhbSessionCount])
      } else {
        summaryDetails = summaryDetails ? summaryDetails : []
      }
    }
    if (highRiskSessionDetails !== undefined) {
      if (highRiskSessionDetails !== null) {
        summaryDetails ? summaryDetails.push(highRiskSessionDetails) : (summaryDetails = [highRiskSessionDetails])
      } else {
        summaryDetails = summaryDetails ? summaryDetails : []
      }
    }
    if (
      isSelfPayEnabled &&
      Programs.getProgramConfigByName(selectedEpisode?.program_name)?.selfPayEligible &&
      (clientDetails?.self_pay_status === CLIENT_SELF_PAY_STATUS.ACTIVE ||
        clientDetails?.self_pay_status === CLIENT_SELF_PAY_STATUS.ELIGIBLE)
    ) {
      summaryDetails?.push({
        label: ' ',
        value: ' ',
        customComponent: (
          <SelfPayStatus
            clientName={clientDetails?.preferred_first_name || clientDetails?.first_name || ''}
            pronouns={clientDetails?.pronouns}
            isActive={clientDetails?.self_pay_status === CLIENT_SELF_PAY_STATUS.ACTIVE}
          />
        ),
      })
    }
    return summaryDetails
  }, [
    sessionCountDetails,
    licenseSessionCount,
    bhbSessionCount,
    highRiskSessionDetails,
    isSelfPayEnabled,
    clientDetails?.self_pay_status,
    clientDetails?.first_name,
    clientDetails?.preferred_first_name,
    clientDetails?.pronouns,
    selectedEpisode?.program_name,
  ])

  const eligibilityCapsuleDetails = useEligibilityCapsuleDetails(
    programName,
    programCoverageBreakdown,
    clientDetails,
    clientCustomer,
  )

  const lyraCareServices = useMemo<LyraCareService[]>(
    () =>
      eligibilityCapsuleDetails.map(({ program, label, link, coverageDescription }) => ({
        id: program,
        label,
        referralLink: isDAEngageProvider ? undefined : link,
        coverageDescription,
      })),
    [eligibilityCapsuleDetails, isDAEngageProvider],
  )

  const parentIntakeLink = selectedEpisode?.external_ids?.qualtrics
    ? parentIntakeQualtricsUrl(selectedEpisode.external_ids.qualtrics as string)
    : undefined

  const shouldDisplayParentIntakeLink =
    selectedEpisode?.program_name === ProgramNames.TeensTherapy && !!parentIntakeLink
  const hasEligibilityError = useEapEligibilityError({
    programName: selectedEpisode?.program_name,
  })

  const isZeroDollarCostShare =
    clientCustomer?.zeroDollarCostShare === 'true' && clientDetails?.customer_support_health_plan

  const healthPlanStatus = useHealthPlanStatus({
    programName: selectedEpisode?.program_name,
    programCoverageBreakdown,
    status: clientDetails?.health_plan_status,
    clientDetails,
    isZeroDollarCostShare,
  })

  const sessionsRemaining = getClientSessionsRemaining(clientDetails, selectedEpisode?.program_name)
  const sessionLimitCategory = getClientSessionLimitCategory(clientDetails, selectedEpisode?.program_name)

  const selfPayContinuationOfCareStatus = useSelfPayContinuationOfCareStatus(
    clientDetails,
    selectedEpisode?.program_name,
    sessionsRemaining,
  )

  const hpiSelfPay = useHPISelfPayStatus(clientDetails, selectedEpisode?.program_name, programCoverageBreakdown)

  const isSelfPayEligible =
    isSelfPayEnabled &&
    Programs.getProgramConfigByName(selectedEpisode?.program_name)?.selfPayEligible &&
    clientDetails?.customer_support_self_pay &&
    clientDetails?.self_pay_status === CLIENT_SELF_PAY_STATUS.ELIGIBLE &&
    sessionsRemaining === 1

  const canManageCareTeam = useCanManageCareTeam()
  const [isManageCareTeamVisible, setIsManageCareTeamVisible] = useState(false)
  const openManageCareTeam = useCallback(() => setIsManageCareTeamVisible(true), [])
  const clientId = useSelector(getSelectedClientId)
  const [pastCareScope, setPastCareScope] = useState<string | null>(null)
  const [careProviderSelection, setCareProviderSelection] = useState<{ scope: string; providerId: string } | null>(null)
  const currentCareScope = JSON.stringify([authProviderId, clientId])
  useEffect(() => setPastCareScope(null), [clientId, authProviderId, canAccessProviderPairs])
  const careHistory = useProviderPairCareHistory(!showPastCare)
  const careServicesSidePanelData = useCareServicesSidePanelData(showPastCare ? careHistory.currentData : undefined, {
    skip: !showSidePanel,
    onManageTeamPress: canManageCareTeam ? openManageCareTeam : undefined,
  })
  const selectedCareProvider =
    showPastCare && careProviderSelection?.scope === currentCareScope
      ? careHistory.currentData?.find(
          (provider) =>
            provider.participant_lyra_id === careProviderSelection.providerId &&
            provider.participant_lyra_id !== authProviderId &&
            provider.can_message,
        )
      : undefined
  useEffect(() => setCareProviderSelection(null), [clientId, authProviderId, canAccessProviderPairs])
  const handleCareProviderUnavailable = useCallback(() => {
    setCareProviderSelection(null)
    dispatch(
      setToastContent({
        id: 'care-services-pair-unavailable',
        text: intl.formatMessage({
          defaultMessage: 'Unable to open this conversation. Please try again.',
          description: 'Recoverable current provider messaging error',
        }),
        toastType: 'error',
      }),
    )
  }, [dispatch, intl])
  const pastCareGroups = useMemo(() => groupPastCare(careHistory.currentData ?? []), [careHistory.currentData])
  const contactsSidePanelData = useContactsSidePanelData()
  const medicationsSidePanelData = useMedicationsSidePanelData({ skip: !showSidePanel })

  const aiConversationToggleRows = useAiConversationToggleRows({
    currentEpisode: selectedEpisode,
    clientDetails,
    clientId,
    providerId: authProviderId,
    skip: !showSidePanel,
  })

  const inlineConcurrentCare = showSidePanel ? [] : concurrentCare

  const sponsoredBenefitsSidePanelData = useGetSponsoredBenefitsSidePanelData({
    clientDetails,
    clientCustomer,
    selectedEpisode,
    benefitsSummaryDetails,
    sessionCountDetails,
    bhbSessionCount,
    highRiskSessionDetailsLoaded: highRiskSessionDetails !== undefined,
    hpiSelfPay,
    selfPayContinuationOfCareStatus,
    healthPlanStatus,
    sessionsRemaining,
    sessionLimitCategory,
    hasEligibilityError,
    isZeroDollarCostShare,
    isLyraSelect,
    benefitsResetDate,
    isDAEngageProvider,
  })

  useEffect(() => {
    if (isPDFContentInitialized || !selectedEpisode || !setClientSummaryPDFContent) {
      return
    }
    if (
      ![
        benefitsResetDate,
        benefitsSummaryDetails,
        clientDetails,
        eligibilityCapsuleDetails,
        healthPlanStatus,
        isLyraSelect,
        sessionSummaryDetails,
        sessionSummaryTitle,
      ].some(isNil)
    ) {
      setClientSummaryPDFContent(
        <BenefitsSummaryPDF
          customerName={clientDetails?.displayed_employer ?? '-'}
          benefitsResetDate={benefitsResetDate}
          benefitsSummaryDetails={benefitsSummaryDetails}
          eligibilityCapsuleDetails={eligibilityCapsuleDetails}
          sessionSummaryDetails={sessionSummaryDetails}
          sessionSummaryTitle={sessionSummaryTitle}
          isLyraSelect={isLyraSelect}
          hasEligibilityError={hasEligibilityError ?? false}
          clientFirstName={clientDetails?.preferred_first_name || clientDetails?.first_name || ''}
          healthPlanStatus={healthPlanStatus}
          isDAEngageProvider={isDAEngageProvider}
        />,
      )
      setIsPDFContentInitialized(true)
    }
  }, [
    benefitsResetDate,
    benefitsSummaryDetails,
    clientDetails,
    eligibilityCapsuleDetails,
    hasEligibilityError,
    healthPlanStatus,
    isLyraSelect,
    selectedEpisode,
    sessionSummaryDetails,
    sessionSummaryTitle,
    setClientSummaryPDFContent,
    isPDFContentInitialized,
    isDAEngageProvider,
  ])
  const { height } = useWindowDimensions()
  const bottomAndTopMargin = 50

  const { data: allAppointments = [], isLoading: isAllAppointmentsLoading } = useClientAppointmentsData()
  const { data: providerEpisodes = [] } = useEpisodesForSelectedClient()
  const { data: allClientEpisodes = [] } = useAllEpisodesForSelectedClient()
  const { appointments: upcomingAppointments, isLoading: isUpcomingAppointmentsLoading } =
    usePatientUpcomingAppointments(isDBTTeensEnabled)
  // Episode-scoped `getNotes` deliberately provides no cache tags, so `updateNote` cannot invalidate
  // it. Without this override a note completed from the Tasks panel leaves its task on screen when the
  // provider navigates back. Scoped to the flag: this query also feeds the pre-existing notes banner,
  // which has always run on the default 20-minute refetch window — only the Tasks panel's write path
  // introduces the staleness this override fixes, so the forced refetch shouldn't cost every provider.
  const { data: episodeNotes = [], isLoading: isEpisodeNotesLoading } = useGetNotesQuery(
    selectedEpisode?.id ? { episodeId: selectedEpisode.id } : skipToken,
    { refetchOnMountOrArgChange: isClientHeaderRedesignEnabled },
  )

  const contents = useSelector(getLyraTherapyContentsData)
  const isFlexibleNoteProvider = Boolean(
    enableDAFlexibleNote && contents?.some((c) => c.name === DA_FLEXIBLE_NOTE_NAME),
  )

  const taskEligibleAppointments = useMemo(
    () => getTaskEligibleAppointments(allAppointments, selectedEpisode?.id, authProviderId),
    [allAppointments, selectedEpisode?.id, authProviderId],
  )

  const apptsThatNeedNotes = useAppointmentsThatNeedNotes(
    taskEligibleAppointments,
    episodeNotes,
    selectedEpisode?.id,
    isFlexibleNoteProvider,
  )

  const sessionsNeedingNotes: number[] = useMemo(() => {
    if (!selectedEpisode?.id) return []

    if (isDAEngageProvider) {
      return taskEligibleAppointments
        .filter((appt: Appointment) => apptsThatNeedNotes.has(appt.appointmentId))
        .map((appt: Appointment) => appt.sessionNumber)
        .sort((a: number, b: number) => a - b)
    }

    return getSessionsNeedingNotesForEpisode(taskEligibleAppointments, episodeNotes, selectedEpisode.id)
  }, [episodeNotes, selectedEpisode?.id, isDAEngageProvider, apptsThatNeedNotes, taskEligibleAppointments])

  const clientTasks = useClientTasks(
    allAppointments,
    sessionsNeedingNotes,
    selectedEpisode?.id,
    episodeNotes,
    isDAEngageProvider,
    authProviderId,
  )
  const videoSessionAppointmentId = useSelector(getLTVideoSessionAppointmentId)
  const navigateToCloseSession = useNavigateToCloseSession({
    originRoute: 'overview',
    skipNotes: !isClientHeaderRedesignEnabled,
  })
  const { sessionTypes: npSessionTypesToSchedule, isLoading: isNpSessionsLoading } = useNpSessionsToSchedule({
    clientId: clientDetails?.id,
    isEnabled: isNpEvaluationVisible,
  })
  const npScheduleTask = mapNpSessionsToScheduleListItem({ sessionTypes: npSessionTypesToSchedule, intl })
  const isTasksModuleLoading =
    isAllAppointmentsLoading ||
    isEpisodeNotesLoading ||
    isNpSessionsLoading ||
    (isDAEngageProvider && navigateToCloseSession.isLoadingNotes)
  const noteNavigationActions = useNoteNavigationActions({
    appointments: allAppointments,
    episodes: allClientEpisodes,
    notes: episodeNotes,
    originRoute: 'overview',
    selectEpisode: setClientDetailsSelectedEpisode,
  })
  const tasksModule =
    isClientHeaderRedesignEnabled && clientDetails?.id ? (
      <TasksModule
        isLoading={isTasksModuleLoading}
        items={[
          ...(npScheduleTask ? [npScheduleTask] : []),
          ...clientTasks.map((task, index) =>
            mapProviderTaskToListItem({
              task,
              clientId: clientDetails.id,
              intl,
              index,
              isDAEngageProvider,
              videoSessionAppointmentId,
              navigateToCloseSession,
              noteNavigationActions,
            }),
          ),
        ]}
      />
    ) : undefined

  const onReferralFormPress = () => {
    if (isDAEngageProvider) {
      track({ event: mixpanelEvents.CLICK_CARE_REFERRAL_FORM_POP_UP })
    }
    window.open(
      'https://provider-support.lyrahealth.com/hc/en-us/restricted?return_to=https%3A%2F%2Fprovider-support.lyrahealth.com%2Fhc%2Fen-us%2Frequests%2Fnew%3Fticket_form_id%3D360000119987',
      '_blank',
    )
  }

  const episodeAppointments = allAppointments.filter((a: Appointment) => a.episodeId === selectedEpisode?.id)
  const guardianInfo = getGuardianInfoForEpisode(episodeAppointments, selectedEpisode?.participants)
  const hasMinorGuardianContactInformation = Boolean(guardianInfo.guardianLyraId)

  const now = new Date()
  const clientAge = clientDetails?.date_of_birth
    ? differenceInYears(now, parseISO(clientDetails.date_of_birth))
    : undefined

  const { isTeenLedProfileBannerEnabled, isDAProviderMigrated, isDAMinorProgramEnabled } = useFlags()
  const clientFirstName = clientDetails?.preferred_first_name || clientDetails?.first_name || ''
  const { riskAlert, riskSummary } = useRiskOverview({ clientId, clientFirstName })
  const sessionNumber = clientDetails?.session_number

  const showIsTeenLedProfileBanner =
    isTeenLedProfileBannerEnabled &&
    isDAEngageProvider &&
    isDAProviderMigrated &&
    isDAMinorProgramEnabled &&
    !hasMinorGuardianContactInformation &&
    Boolean(clientFirstName) &&
    clientAge !== undefined &&
    clientAge < 18 &&
    sessionNumber != null &&
    sessionNumber <= 1

  // Calculate the 18th birthday and whether to show the turning 18 banner
  // Banner should show 1 month before the 18th birthday and continue after they turn 18
  const eighteenthBirthday = clientDetails?.date_of_birth ? addYears(parseISO(clientDetails.date_of_birth), 18) : null
  const oneMonthBeforeEighteenthBirthday = eighteenthBirthday ? subMonths(eighteenthBirthday, 1) : null
  const shouldShowTurning18Banner = oneMonthBeforeEighteenthBirthday ? now >= oneMonthBeforeEighteenthBirthday : false

  const showAdolescentTurning18Banner =
    isDAEngageProvider &&
    isDAProviderMigrated &&
    isDAMinorProgramEnabled &&
    hasMinorGuardianContactInformation &&
    selectedEpisode?.program_name === ProgramNames.DirectAccessIndividualAdolescentTherapy &&
    shouldShowTurning18Banner

  const eighteenthBirthdayFormatted = eighteenthBirthday ? format(eighteenthBirthday, 'MMMM d, yyyy') : ''

  const handleOpenChat = useCallback(() => {
    if (setForceMessagingHubToOpenPopover) {
      setForceMessagingHubToOpenPopover(true)
    }
  }, [setForceMessagingHubToOpenPopover])

  const resolveFullAppointment = (upcoming: PatientUpcomingAppointment): Appointment | undefined => {
    return allAppointments.find((appointment) => appointment.appointmentId === upcoming.appointmentId)
  }

  const openSessionActionScreen = (
    upcoming: PatientUpcomingAppointment,
    { daEvent, action, route }: { daEvent: string; action: string; route: string },
  ) => {
    const appointment = resolveFullAppointment(upcoming)
    if (!appointment) {
      return
    }
    track(
      isDAEngageProvider
        ? {
            event: daEvent,
            details: {
              appointmentId: appointment.appointmentId,
              entryPoint: 'benefitsOverview',
            },
          }
        : {
            event: mixpanelEvents.BUTTON_PRESS,
            action,
            details: { appointmentId: appointment.appointmentId },
          },
    )
    // The upcoming sessions module lists the provider's sessions across all of their episodes, but the
    // cancel/reschedule screens derive program config, billing, and availability from the currently
    // selected episode.
    const owningEpisode = providerEpisodes.find((episode) => episode.id === appointment.episodeId)
    if (owningEpisode) {
      setClientDetailsSelectedEpisode?.(owningEpisode)
    }
    dispatch(setAppointment(appointment))
    navigate(route, { state: { returnRoute: CLIENT_HOME.route } })
  }

  const handleReschedulePress = (upcoming: PatientUpcomingAppointment) =>
    openSessionActionScreen(upcoming, {
      daEvent: mixpanelEvents.RESCHEDULE_SESSION,
      action: actions.RESCHEDULE_SESSION,
      route: CLIENTS_RESCHEDULE_SESSION_DETAILS.route,
    })

  const handleCancelPress = (upcoming: PatientUpcomingAppointment) =>
    openSessionActionScreen(upcoming, {
      daEvent: mixpanelEvents.CANCEL_SESSION,
      action: actions.CANCEL_SESSION,
      route: CLIENTS_CANCEL_SESSION.route,
    })

  const upcomingSessionsSubtitle = isDBTTeensProgram
    ? intl.formatMessage(
        {
          defaultMessage:
            "You’ll be working alongside {clientFirstName}'s care team to manage {possessivePronoun} treatment.",
          description: "Explains a DBT Teens provider's role alongside the client's care team",
        },
        {
          clientFirstName,
          possessivePronoun: getPossessivePronoun(clientDetails?.pronouns),
        },
      )
    : undefined

  const upcomingSessionsModule = isDBTTeensEnabled ? (
    <UpcomingSessionsModule
      appointments={upcomingAppointments}
      isLoading={isUpcomingAppointmentsLoading || isAllAppointmentsLoading}
      authProviderId={authProviderId}
      displayTimeZone={browserTimeZone}
      subtitle={upcomingSessionsSubtitle}
      onViewAllPress={() => navigate(CLIENTS_SESSIONS.route)}
      onSchedulePress={
        selectedEpisode?.state !== episodeStates.IN_PROGRESS
          ? undefined
          : () => navigate(CLIENTS_NEW_SESSION.route, { state: { scheduleSessionOrigin: location.pathname } })
      }
      onReschedulePress={handleReschedulePress}
      onCancelPress={handleCancelPress}
    />
  ) : undefined

  // Early return if episode is not loaded yet to prevent rendering with incomplete data
  if (!selectedEpisode) {
    return null
  }

  return (
    <>
      <BenefitsSummary
        customerName={clientDetails?.displayed_employer ?? '-'}
        benefitsResetDate={benefitsResetDate}
        benefitsSummaryDetails={benefitsSummaryDetails ?? []}
        eligibilityCapsuleDetails={eligibilityCapsuleDetails}
        sessionSummaryDetails={sessionSummaryDetails}
        sessionSummaryTitle={sessionSummaryTitle}
        parentIntakeLink={parentIntakeLink}
        shouldDisplayParentIntakeLink={shouldDisplayParentIntakeLink}
        isLyraSelect={isLyraSelect}
        hasEligibilityError={hasEligibilityError ?? false}
        clientFirstName={clientFirstName}
        healthPlanStatus={hpiSelfPay.isActive ? hpiSelfPay.inlineMessage : healthPlanStatus}
        selfPayContinuationOfCareStatus={hpiSelfPay.isActive ? undefined : selfPayContinuationOfCareStatus}
        selfPayEligible={hpiSelfPay.isActive ? false : isSelfPayEligible}
        hpiSelfPayWarningBanner={hpiSelfPay.banner}
        careProgressSummaryData={careProgressSummaryData}
        concurrentCare={inlineConcurrentCare}
        sessionsNeedingNotes={sessionsNeedingNotes}
        combineProgramSectionsAndShowReferral={episodeProgramConfig?.combineProgramSectionsAndShowReferral}
        onReferralFormPress={onReferralFormPress}
        isDAEngageProvider={isDAEngageProvider}
        clientAge={clientAge}
        clientPronouns={clientDetails?.pronouns}
        onOpenChat={setForceMessagingHubToOpenPopover ? handleOpenChat : undefined}
        showIsTeenLedProfileBanner={showIsTeenLedProfileBanner}
        showAdolescentTurning18Banner={showAdolescentTurning18Banner}
        eighteenthBirthdayFormatted={eighteenthBirthdayFormatted}
        programName={programName}
        clinicalQualityInsights={clinicalQualityInsights}
        upcomingSessionsModule={upcomingSessionsModule}
        riskAlert={riskAlert}
        riskSummary={riskSummary}
        recommendedActions={
          <RecommendedActions
            title={intl.formatMessage({
              defaultMessage: 'Recommended actions',
              description: 'Heading above the list of things a provider should do next for a member',
            })}
            actions={npTestPlanRecommendedActions}
          />
        }
        showSummaryCard={isNpEvaluationVisible}
        tasksModule={tasksModule}
        hasTasksModuleItems={isClientHeaderRedesignEnabled && clientTasks.length > 0}
        shouldHideEligiblePrograms={shouldHideEligiblePrograms}
        showSidePanel={showSidePanel}
        sponsoredBenefitsSidePanelData={sponsoredBenefitsSidePanelData}
        aiConversationToggleRows={aiConversationToggleRows}
        careServicesSidePanelData={careServicesSidePanelData}
        onMessageCareProvider={
          showPastCare
            ? (providerId) => {
                setPastCareScope(null)
                setCareProviderSelection({ scope: currentCareScope, providerId })
              }
            : undefined
        }
        onViewPastCare={
          showPastCare && pastCareGroups.length
            ? () => {
                setCareProviderSelection(null)
                setPastCareScope(currentCareScope)
              }
            : undefined
        }
        contactsSidePanelData={contactsSidePanelData}
        medicationsSidePanelData={medicationsSidePanelData}
        onViewServicesAndBenefitsPress={
          isClientHeaderRedesignEnabled ? () => setIsSponsoredBenefitsModalVisible(true) : undefined
        }
      />
      {showPastCare && pastCareScope === currentCareScope && clientId && (
        <PastCareModal
          key={currentCareScope}
          clientId={clientId}
          clientName={clientDetails?.full_name || clientFirstName}
          groups={pastCareGroups}
          onClose={() => setPastCareScope(null)}
        />
      )}
      {canManageCareTeam && isManageCareTeamVisible && (
        <ManageCareTeamModal onRequestClose={() => setIsManageCareTeamVisible(false)} />
      )}
      {selectedCareProvider && clientId && (
        <ProviderPairMessagePanel
          key={JSON.stringify([currentCareScope, selectedCareProvider.participant_lyra_id])}
          clientId={clientId}
          clientName={clientDetails?.full_name || clientFirstName}
          counterpart={selectedCareProvider}
          onClose={() => setCareProviderSelection(null)}
          onUnavailable={handleCareProviderUnavailable}
          testIDPrefix='CareServices'
        />
      )}
      <SponsoredBenefitsModal
        visible={isSponsoredBenefitsModalVisible}
        clientName={clientDetails?.full_name || clientFirstName}
        sponsorName={clientDetails?.displayed_employer}
        lyraCareServices={lyraCareServices}
        onRequestClose={() => setIsSponsoredBenefitsModalVisible(false)}
      />
      <Modal
        visible={!!modalData}
        modalContents={modalData || ''}
        onRequestClose={() => setModalData(null)}
        onCloseEnd={() => setModalData(null)}
        scrollable
        isHeaderTransparent
        scrollableModalHeight='auto'
        closeOnScrim
        scrollableStyle={{ height: height - bottomAndTopMargin }}
      />
    </>
  )
}

export default BenefitsSummaryContainer
